import { useState, useEffect, useRef } from "react";

import { useLocation, useNavigate } from 'react-router-dom';
import { withTranslation } from "react-i18next";
import { isEqual } from "lodash";
import isEmail from 'validator/lib/isEmail';

import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import AppCard from "@plannel/components/ui/AppCard";
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import PersonAdd from '@mui/icons-material/PersonAdd';

import authService from "@plannel/services/system/auth-service";
import systemService from "@plannel/services/system/system-service";
import { RouteList } from "@plannel/routeList";
import i18next from "@plannel/utils/i18n";
import logo from "@plannel/assets/icons/main-logo.png";
import debug from "@plannel/utils/debug-util";
import ColorUtil from "@plannel/pages/utils/color-util";

import PlannelFooter from "@plannel/components/PlannelFooter";
import PasswordInput from "@plannel/components/PasswordInput";
import Snackbar from "@plannel/components/Snackbar";
import SelectLanguage from "@plannel/components/SelectLanguage";
import BackToSignIn from "@plannel/components/BackToSignIn";
import PasswordPolicyContainer from "@plannel/components/PasswordPolicyContainer";
import { FS } from "@plannel/utils/font-util";

const TENANT = "tenant";
const USERNAME = "username";
const PASSWORD = "password";
const CONFIRM_PASSWORD = "confirmPassword";
const TOKEN = "token";

const MSG_FIELD_REQUIRED = "MSG_FieldRequired";
const Signup = ({ t }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const verifyParams = useRef({ tenant: "", username: "", signupToken: "" });

  const [isValidToken, setIsValidToken] = useState(true);
  const [showPassword, setShowPassword] = useState({ password: false, confirmPassword: false });
  const [completed, setCompleted] = useState(false);
  const [errors, setErrors] = useState({});
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [msgObj, setMsgObj] = useState({ message: "" });
  const defaultUserInfo = { tenant: "", username: "", password: "", firstName: "", lastName: "", dispName: "", deptName: "", postn: null, roles: [], activeFlg: true, delFlg: false };
  const [newUser, setNewUser] = useState(defaultUserInfo);
  const { snackBarMessageColor } = ColorUtil;

  const setPasswordErrorMessage = (data) => {
    if (i18next.exists(data.message)) {
      const messageWithCount = ["MSG_PasswordDCredit", "MSG_PasswordLCredit", "MSG_PasswordOCredit", "MSG_PasswordUCredit", "MSG_PasswordRepeat"];
      if (data.details?.count && messageWithCount.includes(data.message)) {
        return t(data.message, { N: data.details.count });
      }
      if (data.details?.min && data.details?.max && isEqual(data.message, "MSG_CheckPasswordLength")) {
        return t(data.message, { min: data.details.min, max: data.details.max });
      }

      return t(data.message);
    }

    return t("MSG_NetworkError");
  }

  const verifyToken = () => {
    systemService.verifySignupToken(verifyParams.current)
      .catch(error => {
        debug(error);
        const { message } = error.response.data;
        setMsgObj({ message });
        setIsValidToken(false);
      });
  }

  const handleSelectedUser = (e) => {
    const targetName = e.target.name;
    const targetValue = e.target.value;

    if(targetValue.length === 0) {
      setErrors({ ...errors, [targetName]: MSG_FIELD_REQUIRED });
    } else {
      setErrors({ ...errors, [targetName]: null });
    }

    setNewUser({
      ...newUser,
      [targetName]: targetValue
    });
  }

  const onBlurUsername = (e) => {
    if (e.target.value.length > 0) {
      if (!isEmail(e.target.value)) {
        setErrors({ ...errors, username: "MSG_CheckUsername" })
      }
    }
  }

  const onBlurPassword = (e) => {
    const enteredPassword = e.target.value;
    if (enteredPassword?.length > 0) {
      systemService.verifyPassword(enteredPassword)
        .then((response) => {
          const results = response.data;
          if (results.code) {
            const returnErrors = { ...errors, password: setPasswordErrorMessage(results) }
            setErrors(returnErrors);
          } else {
            if (enteredPassword !== newUser[CONFIRM_PASSWORD]) {
              setErrors({ ...errors, confirmPassword: "MSG_NotEqualPassword" });
            } else {
              setErrors({ ...errors, confirmPassword: null });
            }
          }
        })
    }
  }

  const onBlurConfirmPassword = (e) => {
    if (e.target.value.length > 0) {
      if (newUser[PASSWORD] !== newUser[CONFIRM_PASSWORD]) {
        setErrors({ ...errors, confirmPassword: "MSG_NotEqualPassword" });
      }
    }
  }

  const handleResponse = () => {
    setCompleted(true);
    setMsgObj({ message: "MSG_SignupComplete" });
  }

  const checkValidation = () => {
    let currentErrors = errors;

    if (newUser[USERNAME].length === 0) {
      currentErrors = { ...currentErrors, username: MSG_FIELD_REQUIRED };
    }

    if (newUser[PASSWORD].length === 0) {
      currentErrors = { ...currentErrors, password: MSG_FIELD_REQUIRED };
    }

    if (newUser[CONFIRM_PASSWORD].length === 0) {
      currentErrors = { ...currentErrors, confirmPassword: MSG_FIELD_REQUIRED };
    }

    if (newUser[PASSWORD] !== newUser[CONFIRM_PASSWORD]) {
      currentErrors = { ...currentErrors, confirmPassword: "MSG_NotEqualPassword" };
    }

    setErrors(currentErrors);

    return currentErrors;
  }

  const saveUser = () => {
    authService.signup(newUser)
      .then(() => {
        handleResponse();
      })
      .catch(error => {
        const { message, details } = error.response.data;

        let errorMsg = message ? t(message) : t("MSG_SaveFailed");
        if (details?.count && (isEqual(message, "MSG_PasswordDCredit") || isEqual(message, "MSG_PasswordLCredit") || isEqual(message, "MSG_PasswordOCredit") ||
          isEqual(message, "MSG_PasswordUCredit") || isEqual(message, "MSG_PasswordRepeat"))) {
          errorMsg = t(message, { N: details.count });
        }
        if (details?.min && details?.max && isEqual(message, "MSG_CheckPasswordLength")) {
          errorMsg = t(message, { min: details.min, max: details.max });
        }

        setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: errorMsg });
        debug(error);
      });
  }

  const handleRegister = () => {
    const currentErrors = checkValidation();

    if (Object.keys(currentErrors) === 0) {
      saveUser();
    } else {
      let checkError = null;

      for (const value of Object.values(currentErrors)) {
        if (value !== null) {
          checkError = value;
        }
      }

      if (checkError === null) {
        saveUser();
      }
    }
  }

  useEffect(() => {
    if (!location?.search) {
      navigate(RouteList.Signin.path);
    } else {
      const urlSearchVerifyParams = new URLSearchParams(location.search);
      const usernameParams = urlSearchVerifyParams.get(USERNAME);
      const tenantParams = urlSearchVerifyParams.get(TENANT);
      const signupToken = urlSearchVerifyParams.get(TOKEN);

      verifyParams.current = { tenant: tenantParams, username: usernameParams, signupToken };

      verifyToken();

      setNewUser({ ...newUser, username: usernameParams, tenant: tenantParams });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Container id={"signup"} component="main" maxWidth="sm">
      < CssBaseline />
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }} >
        <Box mt={8} />
        <img src={logo} width={170} alt="loading..." />
        {
          !isValidToken || completed ? "" : (
            <AppCard sx={{ mt: 2 }}>
              <CardContent sx={{ px: '30px', pb: '16px !important', display: "flex", flexDirection: "column", alignItems: "center" }}>
                <PersonAdd />
                <Typography variant="h6" fontSize={FS.KPI} mb={2}>{t("signUp")}</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      required
                      fullWidth
                      id={TENANT}
                      label={t(TENANT)}
                      name={TENANT}
                      autoComplete="off"
                      value={newUser.tenant}
                      error={Boolean(errors.tenant)}
                      helperText={(t(errors.tenant))}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      required
                      fullWidth
                      id={USERNAME}
                      label={t("emailAddress")}
                      name={USERNAME}
                      autoComplete="off"
                      value={newUser.username}
                      onChange={handleSelectedUser}
                      error={Boolean(errors.username)}
                      helperText={(t(errors.username))}
                      disabled
                      onBlur={onBlurUsername}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <PasswordInput
                      name={PASSWORD}
                      label={t(PASSWORD)}
                      options={{ onBlur: onBlurPassword, onChange: handleSelectedUser, error: Boolean(errors.password), helperText: (t(errors.password)) }}
                      password={newUser.password}
                      showPassword={showPassword}
                      setShowPassword={setShowPassword}
                      withVisibilityToggle />
                  </Grid>
                  <Grid item xs={12}>
                    <PasswordInput
                      name={CONFIRM_PASSWORD}
                      label={t("passwordConfirmation")}
                      options={{ onBlur: onBlurConfirmPassword, onChange: handleSelectedUser, error: Boolean(errors.confirmPassword), helperText: (t(errors.confirmPassword)) }}
                      password={newUser.confirmPassword}
                      showPassword={showPassword}
                      setShowPassword={setShowPassword}
                      withVisibilityToggle />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      id="lastName"
                      label={t("lastName")}
                      name="lastName"
                      autoComplete="lname"
                      value={newUser.lastName}
                      onChange={handleSelectedUser}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      autoComplete="fname"
                      name="firstName"
                      fullWidth
                      id="firstName"
                      label={t("firstName")}
                      value={newUser.firstName}
                      onChange={handleSelectedUser}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      id="dispName"
                      label={t("displayName")}
                      name="dispName"
                      autoComplete="fname"
                      value={newUser.dispName}
                      onChange={handleSelectedUser}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      id="deptName"
                      label={t("deptName")}
                      name="deptName"
                      autoComplete="fname"
                      value={newUser.deptName}
                      onChange={handleSelectedUser}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <PasswordPolicyContainer />
                  </Grid>
                </Grid>
                <Button fullWidth sx={{ my: 2 }} variant="contained" onClick={handleRegister}>{t("confirm")}</Button>
              </CardContent>
            </AppCard>
          )
        }
      </Box>
      {completed ? <Alert severity="success" sx={{ mt: 1 }}>{t(msgObj.message)}</Alert> : ""}
      {!isValidToken ? <Alert severity="error" sx={{ mt: 1 }}>{t(msgObj.message)}</Alert> : ""}
      <Box sx={{ mt: 1, display: "flex" }}>
        <SelectLanguage />
        <BackToSignIn />
      </Box>
      <PlannelFooter sx={{ pb: 2 }} />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </Container>
  );
};

export default withTranslation()(Signup);